import React from 'react';
import {
  Action,
  ConfirmModal,
  Fieldset,
  FormSubmitType,
  HStack, Message, Portal, RadioButton, SimpleTable, Stack, Text,
  Tooltip,
  useModal,
  useNotifications,
} from '@doveit/bricks';
import objectHash from 'object-hash';
import { pluralizeWord } from '@doveit/hammer';
import { AssignmentWizardStepProps } from '../types';
import SelectContact, { OperationType } from '../../../../../contact/containers/select-contact/SelectContact';
import { AssignmentWizardData, Contact } from '../../../../../providers/api/dtos';
import NoContactView from '../../../../../contact/containers/select-contact/components/no-contact-view/NoContactView';
import WizardCompanyForm, { WizardCompanyFormModel } from '../../../../../company/components/wizard-company-form/WizardCompanyForm';
import { toAssignmentWizardData, toAssignmentWizardSellerModel } from './mappers';
import useContact from '../../../../../contact/hooks/use-contact/useContact';
import createOrUpdateContact from '../../../../../contact/utils/createOrUpdateContact';
import { getContact } from '../../../../../providers/api/contact/contactProvider';
import { ContactStatus } from '../../../../../contact/containers/select-contact/types';
import { ContactState } from '../../../../../contact/types';
import { mergeContact } from '../../../../../contact/utils/mergeContact';
import { hasContactChanged } from '../../../../../contact/containers/select-contact/utils/hasContactChanged';
import { wizardSellerSchema } from './schema';
import { CONTACT_COMPARISON_KEYS } from '../../../../../contact/constants';
import { useProspectDocuments } from '../../../../../prospect/hooks/use-prospect-documents/useProspectDocuments';
import useIsDevice from '../../../../../hooks/use-is-device/useIsDevice';
import { DocumentTypeValue } from '../../../../../domain/types';
import { useAiSuggestedSellers } from '../../../hooks/use-ai-suggested-sellers/useAiSuggestedSellers';
import SuggestedContactTableRow from '../../../../../contact/components/suggested-contact-table-row/SuggestedContactTableRow';
import { AIAnnotationContactData } from '../../../../../providers/api/dtos/documentFile';
import SuggestedContactCard from '../../../../../contact/components/suggested-contact-card/SuggestedContactCard';

export const NO_CONTACTS_INFO_MESSAGE = 'Aggiungi il/i proprietario/i dell\'immobile.';
export const NO_LEGAL_INFO_MESSAGE = 'Aggiungi il rappresentante legale.';
export const FILL_SELLER_DATA_INFO_MESSAGE = 'Compila i dati del venditore e risparmia tempo nella redazione della proposta e nella richiesta del proforma.';
export const LEGAL_PERSON_WITH_MANY_CONTACTS_TOOLTIP = 'Non è possibile indicare la tipologia Persona giuridica in presenza di più contatti.';
export const SOME_CONTACT_HAVE_BEEN_UPDATED_OUTSIDE_MESSAGE = "Dall'ultimo salvataggio, alcuni dati relativi ai venditori sono stati modificati. Verifica la loro correttezza.";
export const SELLERS_MISMATCH_WARNING_MESSAGE = 'Venditori non corrispondenti alla visura';

const AssignmentWizardSeller: React.FC<AssignmentWizardStepProps> = ({
  initialData,
  formRef,
  loading = false,
  prospect,
  onSubmit,
}) => {
  const { addSuccess, addError } = useNotifications();
  const isMobile = useIsDevice('mobile');
  const { data: prospectContact, isLoading: contactProspectLoading } = useContact(prospect.contactId);

  const overwritteContactInfoModal = useModal();

  const [isLegalPerson, setIsLegalPerson] = React.useState<boolean>(!!initialData.isLegalPersonOnly);
  const [contacts, setContacts] = React.useState<ContactState[]>([]);
  const [areContactsDirty, setAreContactsDirty] = React.useState<boolean>(false);
  const [someContactsHaveBeenChangedOutside, setSomeContactsHaveBeenChangedOutside] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [ignoredSellerKeys, setIgnoredSellerKeys] = React.useState<string[]>(initialData.suggestions?.ignoredSellers ?? []);
  const isLegalPersonDisabled = React.useMemo(() => contacts.length > 1, [contacts.length]);

  const { data: prospectDocuments } = useProspectDocuments(prospect?.id);
  const cadastralSurvey = React.useMemo(
    () => prospectDocuments?.find((doc) => doc.type === DocumentTypeValue.CADASTRAL_SURVEY),
    [prospectDocuments],
  );

  const identityDocument = React.useMemo(
    () => prospectDocuments?.find((doc) => doc.type === DocumentTypeValue.IDENTITY_DOCUMENT),
    [prospectDocuments],
  );

  const existingContacts = React.useMemo(
    () => contacts
      .map(({ existingContact, newContactValues }) => mergeContact(existingContact, newContactValues))
      .filter(Boolean) as Contact[],
    [contacts],
  );

  const { cadastralSurveySellers, suggestedSellers } = useAiSuggestedSellers(existingContacts, cadastralSurvey, identityDocument);

  const suggestedSellersNonIgnored = React.useMemo(() => {
    if (!suggestedSellers) return [];

    return suggestedSellers.filter(
      ({ suggestion }) => !ignoredSellerKeys.includes(objectHash(suggestion)),
    );
  }, [ignoredSellerKeys, suggestedSellers]);

  React.useEffect(() => {
    async function loadAllContacts() {
      try {
        let contactsToShow: {
          contact?: Contact,
          overriddenFields?: Partial<Contact>,
          contactUpdatedAt?: string,
        }[] = [];

        if (initialData.contacts) {
          contactsToShow = await Promise.all(
            initialData.contacts.map(async (contact) => {
              if (contact.id) {
                const dbContact = await getContact(contact.id);

                return {
                  contact: dbContact,
                  overriddenFields: contact.overriddenFields,
                  contactUpdatedAt: contact.updatedAt,
                };
              }

              return {
                overriddenFields: {
                  name: contact.name,
                  email: contact.email,
                  phoneNumber: contact.phoneNumber,
                  birthplace: contact.birthplace,
                  birthday: contact.birthday,
                  fiscalCode: contact.fiscalCode,
                  residence: contact.placeOfResidence,
                  identityDocument: contact.identityDocument,
                  citizenship: contact.citizenship,
                },
              };
            }),
          );

          const hasContactsBeenUpdatedExternally = contactsToShow.some(
            ({ contact, contactUpdatedAt, overriddenFields }) => {
              // old wizard data
              if (!contact?.updatedAt || !contactUpdatedAt) {
                return true;
              }

              // check if there are any changes in managed fields
              return hasContactChanged(contact, overriddenFields ?? contact, [...CONTACT_COMPARISON_KEYS, 'identityDocument', 'citizenship']);
            },
          );

          setSomeContactsHaveBeenChangedOutside(
            hasContactsBeenUpdatedExternally,
          );
        } else if (prospectContact) {
          contactsToShow = [{ contact: prospectContact }];
        }

        setContacts(
          contactsToShow.map(({ contact, overriddenFields }) => ({
            internalId: crypto.randomUUID(),
            status: 'OK',
            existingContact: contact,
            newContactValues: overriddenFields,
          })),
        );
      } catch (error) {
        addError('Errore nel caricamento dei contatti.');
      }
    }

    loadAllContacts();
  }, [addError, initialData.contacts, prospectContact]);

  const updateContacts = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const updatedContacts = await Promise.all(contacts
        .map((contactState) => createOrUpdateContact(contactState.newContactValues, contactState.existingContact).then((updatedContact) => ({
          ...contactState,
          existingContact: updatedContact,
          newContactValues: undefined,
        }))));

      addSuccess('I contatti sono stati aggiornati con successo.');

      overwritteContactInfoModal.close();
      setIsSaving(false);
      setContacts(updatedContacts);
      setAreContactsDirty(true);
      setSomeContactsHaveBeenChangedOutside(false);
    } catch (_) {
      addError('Non è stato possibile salvare correttamente i contatti.');
      setIsSaving(false);
    }
  }, [addError, addSuccess, contacts, overwritteContactInfoModal]);

  const handleContactsChange = React.useCallback((
    internalId: string,
    status: ContactStatus,
    existingContact?: Contact,
    newContactValues?: Partial<Contact>,
    operation?: OperationType,
  ) => {
    if (operation === 'DESELECT') {
      // deselect
      setContacts((prevContacts) => prevContacts.filter((contact) => contact.internalId !== internalId));
    } else if (contacts.some((contact) => contact.internalId === internalId)) {
      setContacts((prev) => prev
        .map((contact) => {
          if (contact.internalId !== internalId) return contact;

          return {
            ...contact, internalId, status, existingContact, newContactValues,
          } as ContactState;
        }));
    } else {
      setContacts((prev) => [{
        internalId, status, existingContact, newContactValues,
      } as ContactState, ...prev]);
    }
    setAreContactsDirty(true);
  }, [contacts]);

  const handleSuggestedSellerAdd = React.useCallback((newContactValues: Partial<Contact>) => {
    setContacts((prev) => [...prev, {
      internalId: crypto.randomUUID(),
      status: 'OK',
      existingContact: undefined,
      newContactValues,
    }]);
    setAreContactsDirty(true);
  }, []);

  const handleSuggestedSellerCompare = React.useCallback((updatedContactValues: Partial<Contact>, existingContact: Contact) => {
    setContacts((prev) => prev.map((cs) => (cs.existingContact?.id === existingContact.id ? { ...cs, newContactValues: mergeContact(cs.newContactValues, updatedContactValues) } : cs)));
    setAreContactsDirty(true);
  }, []);

  const handleSuggestedSellerIgnore = React.useCallback((suggestion: AIAnnotationContactData) => {
    setIgnoredSellerKeys((prev) => [...prev, objectHash(suggestion)]);
    setAreContactsDirty(true);
  }, []);

  const onInternalSubmit: FormSubmitType<WizardCompanyFormModel> = React.useCallback(async (formValues: WizardCompanyFormModel) => {
    let savedContacts = contacts.map((c) => c.existingContact!);
    const isSellerInfoFormDirty = (formRef?.current as any)?.dirty ?? false;
    const isLegalPersonOnlyDirty = initialData.isLegalPersonOnly !== isLegalPerson;

    const dirty = isSellerInfoFormDirty || areContactsDirty || isLegalPersonOnlyDirty;

    if (dirty) {
      const contactPromises: Promise<{ savedContact?: Contact, error?: Error, newContactValues?: Partial<Contact> }>[] = contacts
        .map(({ existingContact, newContactValues }) => createOrUpdateContact(newContactValues, existingContact)
          .then((savedContact) => ({ savedContact }))
          .catch((error: Error) => ({
            error,
            newContactValues,
          })));

      const results = await Promise.all(contactPromises);
      const errors = results.filter((result) => !!result.error);
      if (errors.length > 0) {
        errors.forEach((error) => addError(`Si è verificato un errore nel salvataggio del contatto ${error.newContactValues?.name || error.newContactValues?.email || error.newContactValues?.phoneNumber}. Correggi.`));
        return;
      }

      savedContacts = results.map((result) => result.savedContact!);
    }

    const values: AssignmentWizardData = await toAssignmentWizardData(initialData, formValues, savedContacts, isLegalPerson);
    onSubmit({
      ...values,
      suggestions: {
        ignoredCadastralUnits: values.suggestions?.ignoredCadastralUnits ?? [],
        ignoredSellers: ignoredSellerKeys,
      },
    }, dirty);
  }, [addError, areContactsDirty, contacts, formRef, ignoredSellerKeys, initialData, isLegalPerson, onSubmit]);

  return (
    <>
      <Stack gap={300}>
        <Message
          type="neutral"
          message={FILL_SELLER_DATA_INFO_MESSAGE}
        />

        <HStack gap={150}>
          <div>
            <RadioButton
              text="Persona fisica"
              checked={!isLegalPerson}
              onChange={() => setIsLegalPerson(false)}
            />
          </div>

          <div>
            {isLegalPersonDisabled && (
              <Tooltip content={LEGAL_PERSON_WITH_MANY_CONTACTS_TOOLTIP}>
                <div>
                  <RadioButton
                    text="Persona giuridica"
                    checked={isLegalPerson}
                    disabled
                  />
                </div>
              </Tooltip>
            )}
            {!isLegalPersonDisabled && (
              <RadioButton
                text="Persona giuridica"
                checked={isLegalPerson}
                onChange={() => setIsLegalPerson(true)}
              />
            )}
          </div>
        </HStack>

        <Fieldset legend={isLegalPerson ? 'Rappresentante legale' : 'Elenco venditori'} />

        {someContactsHaveBeenChangedOutside && !areContactsDirty && (
          <Message
            type="warning"
            message={SOME_CONTACT_HAVE_BEEN_UPDATED_OUTSIDE_MESSAGE}
            boxed
            aria-label="Messaggio per informare l'utente che i dati del contatto sono stati sovrascritti esternamente"
            actions={[
              <Action
                label="Conferma dati inseriti"
                size="S"
                color="warning"
                emphasis="high"
                onClick={() => overwritteContactInfoModal.open()}
              />,
            ]}
          />
        )}

        {cadastralSurveySellers.length > 0 && suggestedSellersNonIgnored.length === 0 && cadastralSurveySellers.length !== existingContacts.length && (
          <Message
            type="warning"
            boxed
            message="Venditori non corrispondenti alla visura"
            description={(
              <Text.BodySmall>
                Hai indicato {existingContacts.length} {pluralizeWord('venditor', 'e', 'i', existingContacts.length)}  mentre la visura catastale ne presenta {cadastralSurveySellers.length}.
              </Text.BodySmall>
            )}
          />
        )}

        {suggestedSellersNonIgnored.length > 0 && (
          <Message
            type="warning"
            boxed
            message="Venditori suggeriti"
            description={(
              <Text.BodySmall>
                I seguenti venditori sono stati rilevati automaticamente dalla visura catastale.
                <br />
                Puoi aggiungerli al mandato, confrontarli con venditori già presenti o ignorarli.
              </Text.BodySmall>
            )}
          >
            {!isMobile && (
              <SimpleTable aria-label="Tabella venditori suggeriti">
                <SimpleTable.Body>
                  {suggestedSellersNonIgnored.map((sellerSuggestion) => (
                    <SimpleTable.Row key={objectHash(sellerSuggestion.suggestion)} asChild>
                      <SuggestedContactTableRow
                        key={objectHash(sellerSuggestion.suggestion)}
                        sellerSuggestion={sellerSuggestion}
                        onAdd={handleSuggestedSellerAdd}
                        onCompare={handleSuggestedSellerCompare}
                        onIgnore={handleSuggestedSellerIgnore}
                      />
                    </SimpleTable.Row>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            )}

            {isMobile && (
              <Stack aria-label="Lista venditori suggeriti">
                {suggestedSellersNonIgnored.map((suggestion) => (
                  <SuggestedContactCard
                    key={objectHash(suggestion)}
                    suggestedContact={suggestion}
                    onAdd={handleSuggestedSellerAdd}
                    onCompare={handleSuggestedSellerCompare}
                    onIgnore={handleSuggestedSellerIgnore}
                  />
                ))}
              </Stack>
            )}
          </Message>
        )}

        {(!isLegalPerson || contacts.length === 0) && (
          <div>
            <SelectContact
              onContactChange={(status, existing, newValues, operation) => handleContactsChange(crypto.randomUUID(), status, existing, newValues, operation)}
              render={{
                contactSelectedView: ({ openContactFinder }) => (
                  <div>
                    <NoContactView
                      label="Cerca o aggiungi"
                      openContactFinder={openContactFinder}
                    />
                  </div>
                ),
              }}
            />
          </div>
        )}

        {contacts.length === 0 && (
          <Message
            type="info"
            message={isLegalPerson ? NO_LEGAL_INFO_MESSAGE : NO_CONTACTS_INFO_MESSAGE}
          />
        )}

        <Stack gap={150}>
          {contacts.map(({
            internalId, existingContact, newContactValues,
          }) => (
            <SelectContact
              key={internalId}
              onContactChange={(status, existing, newValues, operation) => handleContactsChange(internalId, status, existing, newValues, operation)}
              showClearContact
              showBirthData
              initialContact={existingContact || mergeContact(existingContact, newContactValues)}
              initialOverwriteValues={contacts?.find((c) => c.internalId === internalId)?.newContactValues}
              render={{ noContactView: false }}
              editContactRequiredFields={{
                name: true,
                email: true,
                phoneNumber: true,
                birthPlace: true,
                birthDate: true,
                fiscalCode: true,
                address: true,
                identityDocument: true,
                citizenship: true,
              }}
              showIdentityDocument
              showCitizenship
              editContactSchema={wizardSellerSchema}
            />
          ))}
        </Stack>
        <div style={{ display: isLegalPerson && contacts.length > 0 ? 'block' : 'none' }}>
          <WizardCompanyForm
            initialValues={toAssignmentWizardSellerModel(initialData, prospectContact)}
            formRef={formRef}
            onSubmit={onInternalSubmit}
            disabled={contacts.length === 0}
            loading={loading || contactProspectLoading}
          />
        </div>
      </Stack>
      <Portal>
        <ConfirmModal
          {...overwritteContactInfoModal}
          title="Modifica dati contatto"
          aria-label="Modale per confermare le modifiche ai dati del contatto"
          onAbort={() => overwritteContactInfoModal.close()}
          onConfirm={updateContacts}
          loading={isSaving}
        >
          Confermando l&apos;operazione, i dati inseriti in precedenza saranno confermati.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default AssignmentWizardSeller;
